import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Download, X, ChevronDown, Sliders, Target, FileText } from "lucide-react";
import { formatBytes } from "./useImageCompression";

export default function ImageCard({
  image,
  isProcessing,
  processingImageId,
  comparisonMode,
  onDownload,
  onRemove,
  onUpdateSettings,
  onQuickCompress,
  onPercentageCompress
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [localPercentage, setLocalPercentage] = useState([50]);
  
  const isCurrentlyProcessing = processingImageId === image.id;
  
  const getCompressionRatio = () => {
    if (!image.compressed) return 0;
    return Math.round((1 - image.compressed.size / image.originalSize) * 100);
  };

  if (comparisonMode && image.comparisonData) {
    return (
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 shadow-sm relative transition-colors duration-300">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate flex-1">{image.name}</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(image.id)}
              className="text-slate-400 hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="mb-4 text-sm text-slate-600 dark:text-slate-400">
            Original: {formatBytes(image.originalSize)} • {image.originalWidth}×{image.originalHeight}
            {image.wasConverted && (
              <Badge variant="outline" className="ml-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800">
                HEIC Converted
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['quality', 'balanced', 'maximum'].map((preset) => {
              const data = image.comparisonData[preset];
              if (!data) return null;
              
              const ratio = Math.round((1 - data.size / image.originalSize) * 100);
              const presetLabels = {
                quality: 'High Quality',
                balanced: 'Balanced',
                maximum: 'Maximum'
              };
              
              return (
                <div key={preset} className="text-center">
                  <div className="relative aspect-video bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden mb-2">
                    <img
                      src={data.src}
                      alt={`${preset} preview`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{presetLabels[preset]}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {formatBytes(data.size)} • -{ratio}%
                  </p>
                  <Button
                    size="sm"
                    onClick={() => onDownload({ ...image, compressed: data })}
                    className="mt-2 rounded-full"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 shadow-sm relative transition-colors duration-300">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate flex-1">{image.name}</h3>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => onDownload(image)}
              disabled={!image.compressed || isCurrentlyProcessing}
              className="rounded-full"
              data-testid={`download-image-${image.id}`}
            >
              <Download className="w-3 h-3 mr-1" />
              Download
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(image.id)}
              className="text-slate-400 hover:text-red-500"
              data-testid={`remove-image-${image.id}`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Image Preview */}
          <div className="space-y-4">
            <div className="relative aspect-video bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
              <img
                src={image.compressed?.src || image.originalSrc}
                alt={image.name}
                className="w-full h-full object-contain"
              />
              {isCurrentlyProcessing && (
                <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
                </div>
              )}
              {image.wasConverted && (
                <Badge className="absolute top-2 left-2 bg-purple-500 text-white text-xs">
                  HEIC → JPG
                </Badge>
              )}
              {image.isSvg && (
                <Badge className="absolute top-2 left-2 bg-green-500 text-white text-xs">
                  SVG
                </Badge>
              )}
              {image.isGif && (
                <Badge className="absolute top-2 left-2 bg-pink-500 text-white text-xs">
                  GIF
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Original</p>
                <p className="font-semibold text-slate-900 dark:text-white">{formatBytes(image.originalSize)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{image.originalWidth}×{image.originalHeight}</p>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Compressed</p>
                <p className="font-semibold text-blue-600 dark:text-blue-400">
                  {image.compressed ? formatBytes(image.compressed.size) : '-'}
                </p>
                {image.compressed && (
                  <Badge className="bg-emerald-500 text-white text-xs mt-1">
                    -{getCompressionRatio()}%
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center justify-between w-full p-3 bg-slate-50 dark:bg-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
            >
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Individual Settings</span>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showSettings ? 'rotate-180' : ''}`} />
            </button>

            {showSettings && (
              <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                {/* Quality */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Quality</label>
                    <Badge variant="secondary" className="dark:bg-slate-600">{image.settings.quality}%</Badge>
                  </div>
                  <Slider
                    value={[image.settings.quality]}
                    onValueChange={([val]) => onUpdateSettings(image.id, { quality: val })}
                    min={1}
                    max={100}
                    step={1}
                    className="w-full"
                  />
                </div>

                {/* Format */}
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Format</label>
                  <Select
                    value={image.settings.format}
                    onValueChange={(val) => onUpdateSettings(image.id, { format: val })}
                  >
                    <SelectTrigger className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800">
                      <SelectItem value="jpeg">JPEG</SelectItem>
                      <SelectItem value="png">PNG</SelectItem>
                      <SelectItem value="webp">WebP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Preset */}
                <div>
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">Preset</label>
                  <Select
                    value={image.settings.preset}
                    onValueChange={(val) => onUpdateSettings(image.id, { preset: val })}
                  >
                    <SelectTrigger className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800">
                      <SelectItem value="quality">High Quality</SelectItem>
                      <SelectItem value="balanced">Balanced</SelectItem>
                      <SelectItem value="maximum">Maximum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Target className="w-4 h-4" />
                <span>Quick Compress This Image</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onQuickCompress(50, image.id)}
                  disabled={isCurrentlyProcessing}
                  className="rounded-full text-xs"
                >
                  50 KB
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onQuickCompress(100, image.id)}
                  disabled={isCurrentlyProcessing}
                  className="rounded-full text-xs"
                >
                  100 KB
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onQuickCompress(200, image.id)}
                  disabled={isCurrentlyProcessing}
                  className="rounded-full text-xs"
                >
                  200 KB
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onQuickCompress(500, image.id)}
                  disabled={isCurrentlyProcessing}
                  className="rounded-full text-xs"
                >
                  500 KB
                </Button>
              </div>
            </div>

            {/* Percentage Compress */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Sliders className="w-4 h-4" />
                <span>Compress by Percentage</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Slider
                    value={localPercentage}
                    onValueChange={setLocalPercentage}
                    min={10}
                    max={90}
                    step={5}
                    className="w-full"
                  />
                </div>
                <Badge variant="secondary" className="min-w-[3rem] text-center dark:bg-slate-600">
                  {localPercentage[0]}%
                </Badge>
                <Button
                  size="sm"
                  onClick={() => onPercentageCompress(localPercentage[0], image.id)}
                  disabled={isCurrentlyProcessing}
                  className="rounded-full bg-amber-500 hover:bg-amber-600"
                >
                  Apply
                </Button>
              </div>
            </div>

            {/* EXIF Dialog */}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">
                  <FileText className="w-4 h-4 mr-2" />
                  View Details
                </Button>
              </DialogTrigger>
              <DialogContent className="dark:bg-slate-800">
                <DialogHeader>
                  <DialogTitle className="dark:text-white">Image Details</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b dark:border-slate-700">
                    <span className="text-slate-600 dark:text-slate-400">File Name</span>
                    <span className="font-medium text-slate-900 dark:text-white truncate ml-4">{image.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b dark:border-slate-700">
                    <span className="text-slate-600 dark:text-slate-400">Original Size</span>
                    <span className="font-medium text-slate-900 dark:text-white">{formatBytes(image.originalSize)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b dark:border-slate-700">
                    <span className="text-slate-600 dark:text-slate-400">Dimensions</span>
                    <span className="font-medium text-slate-900 dark:text-white">{image.originalWidth} × {image.originalHeight}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b dark:border-slate-700">
                    <span className="text-slate-600 dark:text-slate-400">Format</span>
                    <span className="font-medium text-slate-900 dark:text-white">{image.originalFormat}</span>
                  </div>
                  {image.compressed && (
                    <>
                      <div className="flex justify-between py-2 border-b dark:border-slate-700">
                        <span className="text-slate-600 dark:text-slate-400">Compressed Size</span>
                        <span className="font-medium text-blue-600">{formatBytes(image.compressed.size)}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-slate-600 dark:text-slate-400">Savings</span>
                        <span className="font-medium text-emerald-600">-{getCompressionRatio()}%</span>
                      </div>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
